import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type {
  ConnectedChargers,
  DashboardSnapshot,
  DashboardWsMessage,
  FreqPoint,
  WsStats,
} from "../types/dashboard";
import { normalizeFopHz } from "../utils/freqHz";
import { useMqttFreq } from "./useMqttFreq";
import { pushEthBpsSample } from "./useEthBpsSeries";
import {
  isUiLoadReduced,
  setUiLoadFromServer,
  subscribeUiLoad,
} from "../lib/uiLoadStore";

const MAX_WS_HISTORY = 500;
const MAX_OCPP_RATE = 500;

type ChargerLogRequestResult = {
  charger_id: string;
  source: "ocpp" | "ws";
  filePath?: string;
  lines: string[];
  truncated?: boolean;
  file_size?: number;
  scanned_bytes?: number;
  limit?: number;
  can_load_more?: boolean;
};

type PendingLog = {
  resolve: (value: ChargerLogRequestResult) => void;
  reject: (err: Error) => void;
};

/** Sidebar shows mains freq + sync on every route — keep MQTT freq/main subscribed. */
const wantsLiveFreq = true;

const initialSnapshot: DashboardSnapshot = {
  connection: "connecting",
  connectionHint: "Connecting…",
  mainSyncStatus: null,
  regionsSyncStatus: null,
  regionSyncById: {},
  freqLive: null,
  freqHistory: [],
  fopConfig: null,
  wsStats: null,
  wsHistory: [],
  connectedChargers: null,
  ocppMsgRate: null,
  ocppMsgRateHistory: [],
  infraSnapshot: null,
  hostLoad: null,
  hostDisk: null,
  lbInFcr: null,
  gwStatsFull: null,
  dashboardSession: null,
  edgeWsEvents: null,
  backendVersion: null,
  frontendBuild: null,
  latestSampleMeta: null,
};

function normalizeFreqPoint(raw: Partial<FreqPoint>): FreqPoint | null {
  const ts_ms = Number(raw.ts_ms);
  const freq_hz = Number(raw.freq_hz);
  if (!Number.isFinite(ts_ms) || !Number.isFinite(freq_hz)) return null;
  const fop_hz = normalizeFopHz(raw.fop_hz);
  return {
    ts_ms,
    freq_hz,
    fop_hz,
    raw_u16: Number.isFinite(raw.raw_u16) ? Number(raw.raw_u16) : 0,
  };
}

async function parseWsPayload(data: unknown): Promise<string> {
  if (typeof data === "string") return data;
  if (data instanceof Blob) return data.text();
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  return String(data);
}

export function useDashboardData() {
  const loadReduced = useSyncExternalStore(subscribeUiLoad, isUiLoadReduced, isUiLoadReduced);
  const wantsWs = !loadReduced;
  const mqttFreq = useMqttFreq(wantsLiveFreq && !loadReduced);
  const bootstrapDone = useRef(false);

  const [snapshot, setSnapshot] = useState<DashboardSnapshot>(initialSnapshot);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [wsReady, setWsReady] = useState(false);
  const pendingLogRef = useRef(new Map<string, PendingLog>());

  const applyWsMessage = useCallback((msg: DashboardWsMessage) => {
    if ("type" in msg && msg.type === "charger_log") {
      const id = String(msg.request_id ?? "");
      const pending = pendingLogRef.current.get(id);
      if (!pending) return;
      pendingLogRef.current.delete(id);
      if (msg.error) {
        pending.reject(new Error(msg.error));
        return;
      }
      pending.resolve({
        charger_id: String(msg.charger_id ?? ""),
        source: msg.source === "ws" ? "ws" : "ocpp",
        filePath: msg.filePath,
        lines: Array.isArray(msg.lines) ? msg.lines : [],
        truncated: msg.truncated,
        file_size: msg.file_size,
        scanned_bytes: msg.scanned_bytes,
        limit: msg.limit,
        can_load_more: msg.can_load_more,
      });
      return;
    }
    if ("type" in msg && msg.type === "ui_load") {
      setUiLoadFromServer({
        paused: msg.paused,
        message: msg.message,
        sinceMs: msg.since_ms ?? null,
        byLogin: msg.by_login ?? null,
      });
      return;
    }
    if ("type" in msg && msg.type === "eth_bps") {
      pushEthBpsSample(msg);
      return;
    }
    setSnapshot((prev) => {
      const next = { ...prev };
      if ("type" in msg) {
        switch (msg.type) {
          case "ws_stats_init": {
            if (msg.ws_stats) next.wsStats = msg.ws_stats;
            if (msg.gw_stats) next.gwStatsFull = msg.gw_stats;
            if (msg.ws_history) {
              next.wsHistory = msg.ws_history.slice(-MAX_WS_HISTORY) as typeof prev.wsHistory;
            }
            if (msg.connected_chargers) next.connectedChargers = msg.connected_chargers;
            if (msg.ocpp_msg_rate) next.ocppMsgRate = msg.ocpp_msg_rate;
            if (msg.ocpp_msg_rate_history) {
              next.ocppMsgRateHistory = msg.ocpp_msg_rate_history.slice(
                -MAX_OCPP_RATE,
              ) as typeof prev.ocppMsgRateHistory;
            }
            if (msg.infra_snapshot) next.infraSnapshot = msg.infra_snapshot;
            if (msg.host_load) next.hostLoad = msg.host_load;
            if (msg.host_disk) next.hostDisk = msg.host_disk;
            if (msg.lb_infcr) next.lbInFcr = msg.lb_infcr;
            if (msg.dashboard_session) next.dashboardSession = msg.dashboard_session;
            if (msg.edge_ws_events) next.edgeWsEvents = msg.edge_ws_events;
            break;
          }
          case "gw_stats": {
            const { type: _t, ...stats } = msg;
            next.gwStatsFull = stats;
            break;
          }
          case "infra_snapshot": {
            const { type: _t, ...payload } = msg;
            next.infraSnapshot = payload;
            break;
          }
          case "host_load": {
            const { type: _t, ...payload } = msg;
            next.hostLoad = payload;
            break;
          }
          case "host_disk": {
            const { type: _t, ...payload } = msg;
            next.hostDisk = payload;
            break;
          }
          case "lb_infcr": {
            const { type: _t, ...payload } = msg;
            next.lbInFcr = payload;
            break;
          }
          case "dashboard_session": {
            const { type: _t, ...payload } = msg;
            next.dashboardSession = payload;
            break;
          }
          case "edge_ws_events": {
            const { type: _t, ...payload } = msg;
            next.edgeWsEvents = payload;
            break;
          }
          case "ws_stats": {
            const { type: _t, ...stats } = msg;
            next.wsStats = stats as WsStats;
            if (stats.timestamp) {
              const point: [string, number] = [stats.timestamp, stats.active_count];
              next.wsHistory = [...prev.wsHistory, point].slice(-MAX_WS_HISTORY);
            }
            break;
          }
          case "connected_chargers": {
            const { type: _t, ...chargers } = msg;
            next.connectedChargers = chargers as ConnectedChargers;
            break;
          }
          case "ocpp_msg_rate": {
            const { type: _t, ...rate } = msg;
            next.ocppMsgRate = rate;
            {
              const ts = rate.timestamp
                ? Date.parse(rate.timestamp) || Date.now()
                : Date.now();
              const point: [number, number, number] = [
                ts,
                rate.in_per_sec,
                rate.out_per_sec,
              ];
              next.ocppMsgRateHistory = [...prev.ocppMsgRateHistory, point].slice(
                -MAX_OCPP_RATE,
              );
            }
            break;
          }
          default:
            break;
        }
      }

      return next;
    });
  }, []);

  const sendPresence = useCallback((topic: string, path: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "presence", topic, path }));
  }, []);

  const requestChargerLog = useCallback(
    (chargerId: string, source: "ocpp" | "ws", limit: number) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error("ws_unavailable"));
      }
      const request_id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      return new Promise<ChargerLogRequestResult>((resolve, reject) => {
        pendingLogRef.current.set(request_id, { resolve, reject });
        try {
          ws.send(
            JSON.stringify({
              type: "charger_log",
              request_id,
              charger_id: chargerId,
              source,
              limit,
            }),
          );
        } catch (err) {
          pendingLogRef.current.delete(request_id);
          reject(err instanceof Error ? err : new Error("ws_send_failed"));
        }
      });
    },
    [],
  );

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${window.location.host}/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsReady(true);
      setSnapshot((prev) => ({
        ...prev,
        connection: "live",
        connectionHint: "OCPP live path connected",
      }));
    };

    ws.onclose = () => {
      wsRef.current = null;
      setWsReady(false);
      for (const pending of pendingLogRef.current.values()) {
        pending.reject(new Error("WebSocket closed"));
      }
      pendingLogRef.current.clear();
      setSnapshot((prev) => ({
        ...prev,
        connection: "disconnected",
        connectionHint: "OCPP WebSocket disconnected",
      }));
      if (!isUiLoadReduced()) {
        reconnectTimer.current = setTimeout(connectWs, 3000);
      }
    };

    ws.onerror = () => {
      setSnapshot((prev) => ({
        ...prev,
        connection: "error",
        connectionHint: "WebSocket error — check proxy credentials",
      }));
    };

    ws.onmessage = async (ev) => {
      try {
        const text = await parseWsPayload(ev.data);
        const msg = JSON.parse(text) as DashboardWsMessage;
        applyWsMessage(msg);
      } catch {
        /* ignore malformed */
      }
    };
  }, [applyWsMessage]);

  useEffect(() => {
    if (!wantsWs) {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
      setWsReady(false);
      return;
    }

    connectWs();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
      setWsReady(false);
    };
  }, [connectWs, wantsWs]);

  // One-shot freq_main bootstrap (small table) — not freq_samples.
  useEffect(() => {
    if (loadReduced) return;
    if (bootstrapDone.current) return;

    let cancelled = false;
    bootstrapDone.current = true;

    (async () => {
      try {
        const res = await fetch("/api/freq/history?seconds=300&maxPoints=600", {
          credentials: "same-origin",
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { points?: Partial<FreqPoint>[] };
        const points = (data.points ?? [])
          .map((p) => normalizeFreqPoint(p))
          .filter((p): p is FreqPoint => p !== null);
        if (points.length) mqttFreq.applyBootstrap(points);
      } catch {
        /* live MQTT will fill buffer */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadReduced, mqttFreq.applyBootstrap]);

  // Merge MQTT frequency into dashboard snapshot.
  useEffect(() => {
    setSnapshot((prev) => ({
      ...prev,
      freqLive: mqttFreq.freqLive,
      freqHistory: mqttFreq.freqHistory,
      fopConfig: mqttFreq.fopConfig,
      latestSampleMeta: mqttFreq.latestSampleMeta,
      connection: mqttFreq.mqttConnected ? "live" : mqttFreq.error ? "error" : "connecting",
      connectionHint: mqttFreq.error
        ? `MQTT: ${mqttFreq.error}`
        : mqttFreq.mqttConnected
          ? ""
          : "Connecting…",
      mainSyncStatus: mqttFreq.mainSyncStatus,
      regionsSyncStatus: mqttFreq.regionsSyncStatus,
      regionSyncById: mqttFreq.regionSyncById,
    }));
  }, [
    mqttFreq.freqLive,
    mqttFreq.freqHistory,
    mqttFreq.fopConfig,
    mqttFreq.latestSampleMeta,
    mqttFreq.mainSyncStatus,
    mqttFreq.regionsSyncStatus,
    mqttFreq.regionSyncById,
    mqttFreq.mqttConnected,
    mqttFreq.error,
  ]);

  // Backend version (no QuestDB).
  useEffect(() => {
    if (loadReduced) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/api/version", { credentials: "same-origin" });
        if (!res.ok || cancelled) return;
        const v = await res.json();
        setSnapshot((prev) => ({
          ...prev,
          backendVersion: v.backendVersion ?? null,
          frontendBuild: v.frontendBuild ?? null,
        }));
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadReduced]);

  return { snapshot, sendPresence, requestChargerLog, wsReady };
}
